import { Injectable } from '@angular/core'
import { AnnotationsCaseTypesClient, CaseType, DeleteCaseTypeRequest } from '@ftr/annotations-contracts'
import { AnnotationsApiClientFactory, AnnotationsService } from '@ftr/api-annotations'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { ApiResult, RemoteData } from '@ftr/foundation'

@Injectable({
  providedIn: 'root',
})
export class CaseTypesService extends AnnotationsService {
  constructor(apiClientFactory: AnnotationsApiClientFactory, regionCache: CourtSystemRegionCache) {
    super(apiClientFactory, regionCache)
  }

  /**
   * @param courtSystemId
   * @param audioSegmentId Optional audio segment ID for shared recordings or orders
   * @returns Case types within a court system
   */
  getCaseTypes(courtSystemId: Uuid, audioSegmentId?: Uuid): ApiResult<CaseType[]> {
    return this.performApiRequest<Awaited<ReturnType<AnnotationsCaseTypesClient['listCaseTypes']>>, CaseType[]>({
      courtSystemId,
      requestFn: client =>
        client.caseTypes.listCaseTypes({
          query: {
            courtSystemId,
            audioSegmentId,
          },
        }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to fetch case types'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  createCaseType(body: Omit<CaseType, 'id'>): ApiResult<CaseType> {
    return this.performApiRequest({
      courtSystemId: body.courtSystemId,
      requestFn: client => client.caseTypes.createCaseType({ body }),
      responseMapFn: result => {
        if (result.status === 409) {
          return RemoteData.failure(new Error(result.body.error))
        } else if (result.status !== 201) {
          return RemoteData.failure(new Error('Failed to create case type'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  updateCaseType(body: CaseType): ApiResult<CaseType> {
    return this.performApiRequest({
      courtSystemId: body.courtSystemId,
      requestFn: client => client.caseTypes.updateCaseType({ body }),
      responseMapFn: result => {
        if (result.status === 409) {
          return RemoteData.failure(new Error(result.body.error))
        } else if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to update case type'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  deleteCaseType(body: DeleteCaseTypeRequest): ApiResult<void> {
    return this.performApiRequest({
      courtSystemId: body.courtSystemId,
      requestFn: client => client.caseTypes.deleteCaseType({ body }),
      responseMapFn: result => {
        if (result.status === 204) {
          return RemoteData.success(undefined)
        }
        return RemoteData.failure(new Error('Failed to delete case type'))
      },
    })
  }
}
